import { BaseLayouter } from './BaseLayouter';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { LayoutData, SpacingMode } from '../LayoutData';
import { AlignmentMode } from '../../field/FieldTypes';
import { UpVectorMode } from '../../field/FieldTypes';
import * as THREE from 'three';

/**
 * LineLayouter2 - Uses proven layout mathematics from HTML Demo
 * Direct POI integration with clean, modern approach
 */
export class LineLayouter2 extends BaseLayouter {
    public readonly defaultSpacing: number = 3.0;
    public readonly defaultDirection: Vector3 = new Vector3(0, 0, 1);
    
    constructor() {
        super(
            'line2',
            'Line layouter with proven HTML Demo mathematics - direct POI integration',
            {
                lineFixedSpacing: 3.0,
                lineDirection: new Vector3(0, 0, 1),
                lineCenterLayout: false,
                alignmentMode: AlignmentMode.Aligned,
                upVectorMode: UpVectorMode.WorldUp
            },
            '1d', // 1D layouter
            [SpacingMode.Fixed] // Lines only support fixed spacing (infinite length)
        );
    }
    
    /**
     * Direct application of layout to POIs - clean and efficient!
     * Uses proven mathematics from your HTML Demo
     */
    applyToObjects(pois: any[], layoutData: LayoutData): void {
        //console.log(`🚀 LineLayouter2: Applying layout to ${pois.length} POIs`);
        
        const spacing = this.getParameter(layoutData, 'lineFixedSpacing', this.defaultSpacing);
        const direction = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        const centerLayout = this.getParameter(layoutData, 'lineCenterLayout', false);
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        //console.log(`🎯 LineLayouter2 Parameters:`);
        //console.log(`  - spacing: ${spacing}`);
        //console.log(`  - direction: (${direction.x}, ${direction.y}, ${direction.z})`);
        //console.log(`  - centerLayout: ${centerLayout}`);
        //console.log(`  - alignmentMode: ${alignmentMode}`);
        //console.log(`  - upVectorMode: ${upVectorMode}`);
        
        pois.forEach((poi, index) => {
            // ============ PROVEN POSITION CALCULATION FROM HTML DEMO ============
            const position = this.calculatePositionProven(index, pois.length, spacing, direction, centerLayout);
            
            // ============ PROVEN ORIENTATION CALCULATION FROM HTML DEMO ============
            const orientation = this.calculateOrientationProven(direction, alignmentMode, upVectorMode);
            
            // Apply to POI using the correct data structure
            this.applyTransformToPOI(poi, position, orientation, index);
        });
        
        //console.log(`✅ LineLayouter2: Layout application complete`);
    }
    
    /**
     * Proven position calculation from HTML Demo
     */
    private calculatePositionProven(index: number, totalCount: number, spacing: number, direction: Vector3, centerLayout: boolean): Vector3 {
        // Calculate offset based on center layout setting
        const offset = centerLayout 
            ? (index - (totalCount - 1) / 2) * spacing  // Centered around origin
            : index * spacing;  // Start from origin (0,0,0)
        
        // Use normalized direction vector for positioning
        const normalizedDirection = direction.clone().normalize();
        const position = normalizedDirection.multiplyScalar(offset);
        
        //console.log(`📍 POI ${index}: offset=${offset.toFixed(2)}, position=(${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})`);
        
        return position;
    }
    
    /**
     * Proven orientation calculation from HTML Demo
     * Uses the 4-mode system: Aligned/Perpendicular × World-Up/Path-Up
     */
    private calculateOrientationProven(direction: Vector3, alignmentMode: AlignmentMode, upVectorMode: UpVectorMode): Vector3 {
        // ============ PROVEN 1D ORIENTATION LOGIC FROM WORKING DEMO ============
        
        // 1. Calculate tangent vector (normalized line direction)
        const tangent = direction.clone().normalize();
        
        // 2. Calculate normal vector (perpendicular to line, horizontal preference)
        let normal: Vector3;
        if (Math.abs(tangent.y) < 0.9) {
            // Line is not vertical - use world up to get horizontal normal
            normal = new Vector3().crossVectors(tangent, new Vector3(0, 1, 0));
            if (normal.length() < 0.001) {
                normal = new Vector3(1, 0, 0); // Fallback
            }
        } else {
            // Line is vertical - use world right to get normal
            normal = new Vector3().crossVectors(tangent, new Vector3(1, 0, 0));
            if (normal.length() < 0.001) {
                normal = new Vector3(0, 0, 1); // Fallback
            }
        }
        normal.normalize();
        
        // 3. Calculate binormal vector (perpendicular to both tangent and normal)
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        // 4. Determine forward direction based on alignment mode
        let desiredForward: Vector3;
        if (alignmentMode === AlignmentMode.Aligned) {
            // ALIGNED: Object follows the line direction (uses normal - horizontal direction)
            desiredForward = normal.clone();
        } else {
            // PERPENDICULAR: Object is perpendicular to line (uses tangent - line direction)
            desiredForward = tangent.clone();
        }
        
        // 5. Determine up direction based on up vector mode
        let desiredUp: Vector3;
        if (upVectorMode === UpVectorMode.WorldUp) {
            desiredUp = new Vector3(0, 1, 0);
        } else {
            // PATH UP: Use binormal (perpendicular to line plane), negated for correct orientation
            desiredUp = binormal.clone().negate();
        }
        
        // 6. Build orthogonal coordinate system using proven logic
        desiredForward.normalize();
        desiredUp.normalize();
        
        // Check for parallelism (prevents gimbal lock)
        const dot = Math.abs(desiredForward.dot(desiredUp));
        if (dot > 0.99) {
            // They're parallel - adjust up vector
            if (upVectorMode === UpVectorMode.WorldUp) {
                if (Math.abs(desiredForward.y) > 0.9) {
                    desiredUp.set(1, 0, 0); // Use world X if forward is mostly Y
                }
            }
        }
        
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Up must stay (0,1,0), calculate right and forward accordingly
            finalUp = new Vector3(0, 1, 0);
            
            const parallelThreshold = 0.99;
            const forwardUpDot = Math.abs(desiredForward.dot(finalUp));
            
            if (forwardUpDot > parallelThreshold) {
                // Forward is parallel to world up - use tangent fallback
                finalForward = tangent.clone().normalize();
                finalRight = new Vector3().crossVectors(finalUp, finalForward);
                finalRight.normalize();
            } else {
                // Normal case: forward is not parallel to up
                finalRight = new Vector3().crossVectors(finalUp, desiredForward);
                finalRight.normalize();
                
                // Recalculate forward to ensure perfect orthogonality
                finalForward = new Vector3().crossVectors(finalRight, finalUp);
                finalForward.normalize();
            }
        } else {
            // PATH UP: Use the normal approach with binormal as up
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            // Calculate right vector: right = up × forward
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            
            // Recalculate up to ensure orthogonality: up = forward × right  
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        // Convert to Euler angles (same as HTML demo)
        const matrix = new THREE.Matrix4();
        matrix.makeBasis(
            new THREE.Vector3(finalRight.x, finalRight.y, finalRight.z),
            new THREE.Vector3(finalUp.x, finalUp.y, finalUp.z),
            new THREE.Vector3(finalForward.x, finalForward.y, finalForward.z)
        );
        
        const euler = new THREE.Euler();
        euler.setFromRotationMatrix(matrix, 'YXZ');
        
        // Convert to degrees (POI expects degrees)
        const eulerDegrees = new Vector3(
            THREE.MathUtils.radToDeg(euler.x),
            THREE.MathUtils.radToDeg(euler.y),
            THREE.MathUtils.radToDeg(euler.z)
        );
        
        //console.log(`🎭 Orientation: mode=${alignmentMode}+${upVectorMode}, euler=(${eulerDegrees.x.toFixed(1)}°, ${eulerDegrees.y.toFixed(1)}°, ${eulerDegrees.z.toFixed(1)}°)`);
        
        return eulerDegrees;
    }
    
    /**
     * Apply transform to POI using correct data structure
     * poi.poidata.position.value = position
     * poi.poidata.rotation.value = rotation
     * poi.transform gets updated automatically via connectTransformToPOIData()
     */
    private applyTransformToPOI(poi: any, position: Vector3, orientation: Vector3, index: number): void {
        //console.log(`🎯 Applying transform to POI ${poi.name || index}:`);
        //console.log(`  Position: (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})`);
        //console.log(`  Orientation: (${orientation.x.toFixed(1)}°, ${orientation.y.toFixed(1)}°, ${orientation.z.toFixed(1)}°)`);
        
        // Apply transformations directly to POI data
        // This triggers the event listeners and updates poi.transform automatically
        poi.poidata.position.value = position;
        poi.poidata.rotation.value = orientation;
        
        // Note: Scale is handled by the original layouter if needed
        // poi.poidata.scale.value = 1.0; // Default scale
    }
    
    // Fallback methods for compatibility (not used when applyToObjects is available)
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const spacing = this.getParameter(layoutData, 'lineFixedSpacing', this.defaultSpacing);
        const direction = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        const centerLayout = this.getParameter(layoutData, 'lineCenterLayout', false);
        
        return this.calculatePositionProven(index, totalCount, spacing, direction, centerLayout);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const direction = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        return this.calculateOrientationProven(direction, alignmentMode, upVectorMode);
    }
    
    // Debug visualization (can be enhanced later)
    createDebugVisualization(layoutData: LayoutData): THREE.Group {
        const group = new THREE.Group();
        group.name = 'LineLayouter2_Debug';
        
        const direction = this.getParameter(layoutData, 'lineDirection', this.defaultDirection);
        const spacing = this.getParameter(layoutData, 'lineFixedSpacing', this.defaultSpacing);
        
        // Create line visualization
        const lineLength = 20; // Visual length for debug
        const start = direction.clone().normalize().multiplyScalar(-lineLength / 2);
        const end = direction.clone().normalize().multiplyScalar(lineLength / 2);
        
        const geometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(start.x, start.y, start.z),
            new THREE.Vector3(end.x, end.y, end.z)
        ]);
        
        const material = new THREE.LineBasicMaterial({ 
            color: 0x00ff00, 
            opacity: 0.5, 
            transparent: true 
        });
        
        const line = new THREE.Line(geometry, material);
        group.add(line);
        
        return group;
    }
    
    // ==================== REQUIRED ABSTRACT METHODS FROM BASELAYOUTER ====================
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // Convert our proven Euler calculation to Quaternion
        const eulerVec = this.calculateOrientation(index, totalCount, layoutData);
        
        const threeEuler = new THREE.Euler(
            THREE.MathUtils.degToRad(eulerVec.x),
            THREE.MathUtils.degToRad(eulerVec.y),
            THREE.MathUtils.degToRad(eulerVec.z),
            'YXZ'
        );
        
        const threeQuat = new THREE.Quaternion().setFromEuler(threeEuler);
        
        // Convert to engine Quaternion
        return new Quaternion(threeQuat.x, threeQuat.y, threeQuat.z, threeQuat.w);
    }
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'lineFixedSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        // Lines don't support uniform spacing (infinite length)
        // Return fixed spacing as fallback
        return this.getFixedSpacingParameter(layoutData);
    }
} 